// src/hooks/useAudioPlayer.js
import { useState, useRef, useEffect, useCallback } from "react";
import { getBallLetter } from "../components/BingoBall"; // Import getBallLetter

/**
 * Custom hook for managing audio playback in the Bingo game.
 * It handles loading, playing, and muting various game sounds.
 * @returns {object} An object containing audio control functions and states.
 */
function useAudioPlayer() {
  // Initialize isMuted state from localStorage, default to true (muted) if not found
  const [isMuted, setIsMuted] = useState(() => {
    try {
      const storedMuteState = localStorage.getItem("isMuted");
      return storedMuteState ? JSON.parse(storedMuteState) : true;
    } catch (error) {
      return true; // Default to muted on error
    }
  });

  const [isAudioPlaying, setIsAudioPlaying] = useState(false); // State to indicate if any audio is playing
  const audioInstances = useRef({}); // Ref to store Audio objects for different sounds

  // Callback to load all necessary audio files based on language
  const loadAllAudio = useCallback((language) => {
    const basePath = process.env.REACT_APP_PUBLIC_URL || "";
    const lang = language;

    if (!audioInstances.current[lang]) {
      audioInstances.current[lang] = {};
    }

    // Load general game sounds
    const soundsToLoad = {
      "startSound": "game_started.mp3",
      "winSound": "win.mp3",
      "loseSound": "not_win.mp3", // Added for specific debugging
    };

    for (let i = 1; i <= 75; i++) {
      const letterPrefix = getBallLetter(i).toLowerCase(); // Get the letter (b, i, n, g, o)
      soundsToLoad[`num_${i}`] = `${letterPrefix}_${i}.mp3`; // Construct path like b_1.mp3, i_16.mp3
    }

    for (const key in soundsToLoad) {
      const audioPath = `${basePath}/audio/${lang}/${soundsToLoad[key]}`;
      if (!audioInstances.current[lang][key]) {
        audioInstances.current[lang][key] = new Audio(audioPath);
        // Set initial volume for newly loaded audio
        audioInstances.current[lang][key].volume = isMuted ? 0 : 0.7;
      }
    }

  }, [isMuted]); // isMuted is a dependency because initial volume depends on it

  // Generic play sound function
  const playSound = useCallback(async (audioKey, language) => {
    if (isMuted) return;
    const audio = audioInstances.current[language]?.[audioKey];
    if (audio) {
      audio.currentTime = 0; // Reset audio to start
      try {
        await audio.play();
        setIsAudioPlaying(true);
        const onEnded = () => {
          setIsAudioPlaying(false);
          audio.removeEventListener("ended", onEnded);
        };
        audio.addEventListener("ended", onEnded);
      } catch (e) {
        setIsAudioPlaying(false);
      }
    } 
  }, [isMuted]);

  // Specific sound playing functions
  const playNumberSound = useCallback((number, language) => {
    playSound(`num_${number}`, language);
  }, [playSound]);

  const playWinSound = useCallback((language) => {
    playSound("winSound", language);
  }, [playSound]);

  const playLoseSound = useCallback((language) => {
    playSound("loseSound", language);
  }, [playSound]);

  // Callback to toggle mute state
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newState = !prev;
      localStorage.setItem("isMuted", JSON.stringify(newState)); // Persist to localStorage
      return newState;
    });
  }, []);

  // Effect to apply volume changes to all loaded audio instances when mute state changes
  useEffect(() => {
    Object.values(audioInstances.current).forEach((langAudios) => {
      Object.values(langAudios).forEach((audio) => {
        if (audio) {
          audio.volume = isMuted ? 0 : 0.7; // Update volume based on isMuted state
        }
      });
    });
  }, [isMuted]);

  // Explicit cleanup function to be returned by the hook
  const cleanupAudio = useCallback(() => {
    Object.values(audioInstances.current).forEach((langAudios) => {
      Object.values(langAudios).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          audio.src = ""; // Clear the audio source to release resources
        }
      });
    });
    audioInstances.current = {}; // Clear the ref
    setIsAudioPlaying(false);
  }, []);

  // Internal useEffect for the hook's own cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return {
    isMuted,
    isAudioPlaying,
    toggleMute,
    playNumberSound,
    playWinSound, // Expose win sound
    playLoseSound, // Expose lose function
    loadAllAudio,
    cleanupAudio, // Expose cleanup function
  };
}

export default useAudioPlayer;
